import React, { useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import { hasPolishCrawlShell, parseCrawlLocale } from '../utils/localePath';

// Public marketing routes
const publicRouteMeta: { [path: string]: { titleKey: string; descriptionKey: string } } = {
  '/': { titleKey: 'meta.home.title', descriptionKey: 'meta.home.description' },
  '/how-it-works': { titleKey: 'meta.howItWorks.title', descriptionKey: 'meta.howItWorks.description' },
  '/why-us': { titleKey: 'meta.whyUs.title', descriptionKey: 'meta.whyUs.description' },
  '/pricing': { titleKey: 'meta.pricing.title', descriptionKey: 'meta.pricing.description' },
  '/roadmap': { titleKey: 'meta.roadmap.title', descriptionKey: 'meta.roadmap.description' },
  '/contact': { titleKey: 'meta.contact.title', descriptionKey: 'meta.contact.description' },
  '/blog': { titleKey: 'meta.blog.title', descriptionKey: 'meta.blog.description' },
  '/start': { titleKey: 'meta.start.title', descriptionKey: 'meta.start.description' },
  '/agentic': { titleKey: 'meta.agentic.title', descriptionKey: 'meta.agentic.description' },
  '/solutions': { titleKey: 'meta.solutions.title', descriptionKey: 'meta.solutions.description' },
  '/faq': { titleKey: 'meta.faq.title', descriptionKey: 'meta.faq.description' },
  '/privacy': { titleKey: 'pages.privacy.meta_title', descriptionKey: 'pages.privacy.meta_desc' },
  '/cookies': { titleKey: 'pages.cookies.meta_title', descriptionKey: 'pages.cookies.meta_desc' },
};

// Private / non-indexable routes
const privateRouteMeta: { [path: string]: { titleKey: string; descriptionKey: string } } = {
  '/dashboard': { titleKey: 'meta.dashboard.title', descriptionKey: 'meta.dashboard.description' },
  '/dashboard/projects': { titleKey: 'meta.dashboard.title', descriptionKey: 'meta.dashboard.description' },
  '/dashboard/portfolio': { titleKey: 'meta.portfolio.title', descriptionKey: 'meta.portfolio.description' },
  '/dashboard/account': { titleKey: 'meta.account.title', descriptionKey: 'meta.account.description' },
  '/dashboard/patterns': { titleKey: 'meta.patterns.title', descriptionKey: 'meta.patterns.description' },
  '/dashboard/technologies': { titleKey: 'meta.technologies.title', descriptionKey: 'meta.technologies.description' },
  '/dashboard/desk': { titleKey: 'meta.desk.title', descriptionKey: 'meta.desk.description' },
  '/projects': { titleKey: 'meta.dashboard.title', descriptionKey: 'meta.dashboard.description' },
  '/compare': { titleKey: 'meta.comparison.title', descriptionKey: 'meta.comparison.description' },
  '/feature-soon': { titleKey: 'meta.featureSoon.title', descriptionKey: 'meta.featureSoon.description' },
  '/wizard': { titleKey: 'meta.wizard.title', descriptionKey: 'meta.wizard.description' },
  '/assessment': { titleKey: 'meta.wizard.title', descriptionKey: 'meta.wizard.description' },
};

const allKnownRoutes = { ...publicRouteMeta, ...privateRouteMeta };
const privatePaths = Object.keys(privateRouteMeta);

/** Breadcrumb labels for key public paths (home + current). */
const BREADCRUMB_PATHS = new Set([
  '/blog',
  '/why-us',
  '/pricing',
  '/start',
  '/faq',
  '/solutions',
  '/how-it-works',
  '/contact',
  '/roadmap',
  '/agentic',
]);

/** '/' must not prefix-match every route. */
function pathMatches(routePath: string, pathname: string): boolean {
  if (routePath === '/') return pathname === '/' || pathname === '';
  return pathname === routePath || pathname.startsWith(`${routePath}/`);
}

function isWorkshopPath(pathname: string): boolean {
  return pathname === '/workshop' || pathname.startsWith('/workshop/');
}

const DynamicPageMeta: React.FC = () => {
  const location = useLocation();
  const { t, i18n } = useTranslation();
  const { pathname } = location;
  const { lang: crawlLang, path: logicalPath } = parseCrawlLocale(pathname);

  const { bestMatchConfig, isPrivate, bestPath } = useMemo(() => {
    if (isWorkshopPath(pathname)) {
      return { bestMatchConfig: null, isPrivate: true, bestPath: pathname };
    }

    const matchingPaths = Object.keys(allKnownRoutes).filter((path) => pathMatches(path, logicalPath));

    if (matchingPaths.length === 0) {
      return { bestMatchConfig: null, isPrivate: false, bestPath: null as string | null };
    }

    matchingPaths.sort((a, b) => b.length - a.length);
    const best = matchingPaths[0];

    return {
      bestMatchConfig: allKnownRoutes[best],
      isPrivate: privatePaths.includes(best),
      bestPath: best,
    };
  }, [logicalPath, pathname]);

  const translatedTitle = bestMatchConfig ? t(bestMatchConfig.titleKey) : '';
  const translatedDesc = bestMatchConfig ? t(bestMatchConfig.descriptionKey) : '';

  const noindex = isPrivate || isWorkshopPath(pathname);

  if (!bestMatchConfig && !noindex) {
    return null;
  }

  const enPath = bestPath === '/' ? '/' : (bestPath || logicalPath);
  const plPath = enPath === '/' ? '/pl' : `/pl${enPath}`;
  const enUrl = `https://www.architectiq.io${enPath === '/' ? '/' : enPath}`;
  const plUrl = `https://www.architectiq.io${plPath}`;
  const canonical = crawlLang === 'pl' && hasPolishCrawlShell(enPath) ? plUrl : enUrl;
  const localized = !noindex && hasPolishCrawlShell(enPath);
  const ogLocale = (crawlLang === 'pl' || i18n.language?.startsWith('pl')) ? 'pl_PL' : 'en_US';
  const homeUrl = crawlLang === 'pl' && hasPolishCrawlShell('/') ? 'https://www.architectiq.io/pl' : 'https://www.architectiq.io/';

  let breadcrumbSchema = null;
  const crumbPath = bestPath && BREADCRUMB_PATHS.has(bestPath) ? bestPath : null;
  if (crumbPath && bestMatchConfig) {
    // Breadcrumb item URLs must match crawl locale (same rule as canonical / hreflang).
    const crumbItemUrl = crawlLang === 'pl' && hasPolishCrawlShell(crumbPath)
      ? (crumbPath === '/' ? homeUrl : `https://www.architectiq.io/pl${crumbPath}`)
      : (crumbPath === '/' ? enUrl : `https://www.architectiq.io${crumbPath}`);
    breadcrumbSchema = {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        {
          '@type': 'ListItem',
          position: 1,
          name: 'Architect-IQ',
          item: homeUrl,
        },
        {
          '@type': 'ListItem',
          position: 2,
          name: translatedTitle.replace(/\s*\|\s*Architect-IQ\s*$/i, '').trim() || translatedTitle,
          item: crumbItemUrl,
        },
      ],
    };
  }

  return (
    <Helmet>
      {translatedTitle ? <title>{translatedTitle}</title> : null}
      {translatedDesc ? <meta name="description" content={translatedDesc} /> : null}
      <html lang={crawlLang === 'pl' || i18n.language?.startsWith('pl') ? 'pl' : 'en'} />
      {!noindex && <link rel="canonical" href={canonical} />}
      {localized && <link rel="alternate" hrefLang="en" href={enUrl} />}
      {localized && <link rel="alternate" hrefLang="pl" href={plUrl} />}
      {localized && <link rel="alternate" hrefLang="x-default" href={enUrl} />}

      {translatedTitle ? <meta property="og:title" content={translatedTitle} /> : null}
      {translatedDesc ? <meta property="og:description" content={translatedDesc} /> : null}
      {!noindex && <meta property="og:url" content={canonical} />}
      <meta property="og:type" content="website" />
      <meta property="og:site_name" content="Architect-IQ" />
      <meta property="og:image" content="https://www.architectiq.io/og-image.png" />
      <meta property="og:locale" content={ogLocale} />
      {localized ? (
        <meta property="og:locale:alternate" content={ogLocale === 'en_US' ? 'pl_PL' : 'en_US'} />
      ) : null}

      <meta name="twitter:card" content="summary_large_image" />
      {translatedTitle ? <meta name="twitter:title" content={translatedTitle} /> : null}
      {translatedDesc ? <meta name="twitter:description" content={translatedDesc} /> : null}
      <meta name="twitter:image" content="https://www.architectiq.io/og-image.png" />

      {noindex && <meta name="robots" content="noindex, nofollow" />}

      {breadcrumbSchema && (
        <script type="application/ld+json">
          {JSON.stringify(breadcrumbSchema)}
        </script>
      )}
    </Helmet>
  );
};

export default DynamicPageMeta;
